import { useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import SntDialog from "@/components/SntDialog/SntDialog";
import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import SntWarningInfo from "@cleanup/components/SntInfo/SntWarningInfo";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import wifiCellularMismatchClient from "@/apis/wifiCellularMismatchClient";

interface WifiCellularMismatchCleanupDialogProps {
  isShow: boolean;
  onClose: () => void;
  onCleanupComplete: () => void;
}

const WifiCellularMismatchCleanupDialog = ({
  isShow,
  onClose,
  onCleanupComplete,
}: WifiCellularMismatchCleanupDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [cleanupHarvested, setCleanupHarvested] = useState(true);
  const [result, setResult] = useState<{ success: boolean; count: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCleanup = async () => {
    if (!cleanupHarvested) {
      setError(translate(LanguageKey.cell_tower_cleanup_no_selection_key));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data } = await wifiCellularMismatchClient.cleanupHarvestedMismatches("MEDIUM");
      setResult({ success: true, count: data.deletedCount });
      if (data.deletedCount > 0) {
        onCleanupComplete();
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setCleanupHarvested(true);
    onClose();
  };

  return (
    <SntDialog
      isShow={isShow}
      onClose={handleClose}
      title={translate(LanguageKey.cell_tower_cleanup_dialog_title_key)}
      saveTxt={translate(LanguageKey.clean_up_key)}
      closeTxt={translate(LanguageKey.close_key)}
      onSave={handleCleanup}
      isShowSaveButton={!result?.success}
      disabled={loading || !cleanupHarvested}
    >
      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : result?.success ? (
        <Alert variant="success">
          {translate(LanguageKey.cell_tower_cleanup_success_key).replace("{0}", result.count.toLocaleString())}
        </Alert>
      ) : (
        <>
          {error && <Alert variant="danger">{error}</Alert>}

          <SntWarningInfo className="mb-3">
            {translate(LanguageKey.cell_tower_cleanup_confirm_key)}
          </SntWarningInfo>

          <SntCheckBox
            checked={cleanupHarvested}
            onChange={(checked) => setCleanupHarvested(checked)}
            label={
              <span>
                <strong>{translate(LanguageKey.cell_tower_cleanup_harvested_option_key)}</strong>
                <div className="text-muted small">
                  {translate(LanguageKey.cell_tower_cleanup_harvested_description_key)}
                </div>
              </span>
            }
          />
        </>
      )}
    </SntDialog>
  );
};

export default WifiCellularMismatchCleanupDialog;
